import type { FormGroup } from '@angular/forms';

import type { DropdownData, DropdownSelection } from '../../interfaces/input.interface';
import type { FormField } from '../../interfaces/form.interface';
import { setDropdownSelected } from '../dropdown-data.utils';
import {
    ReactiveSelectionResolver,
    type ReactiveSelectionResolverConfiguration,
} from '../selection/reactive-selection-resolver';

export type ReactiveDropdownFieldRefreshMode = 'full' | 'optionsOnly';

export interface ReactiveDropdownSyncFormBuilder<TModel> {
    readonly model?: FormGroup | null;
    updateForm(data: Partial<TModel> | Record<string, unknown>, emit: boolean): void;
    requestExternalDropdownMappingsRefresh(): void;
    requestExternalDropdownMappingsRefreshForFields?(fieldNames: readonly string[]): void;
    requestExternalDropdownOptionMappingsRefreshForFields?(fieldNames: readonly string[]): void;
}


export interface ReactiveDropdownFieldLookupSection<TModel> {
    readonly fields?: readonly FormField<TModel>[];
}

export interface ReactiveDropdownFieldBindingOptions<TModel, TEntity> {
    readonly field: FormField<TModel> | null | undefined;
    readonly dropdown: DropdownData<TEntity>;
    readonly valuePrimaryKey?: string;
    readonly controlValueKey?: string;
    readonly backendValueKey?: string;
}

export interface ReactiveDropdownMappingsRefreshOptions<TModel> {
    readonly formBuilder: ReactiveDropdownSyncFormBuilder<TModel> | null | undefined;
    readonly beforeRefresh?: () => void;
}

export interface ReactiveDropdownFieldMappingsRefreshOptions<TModel> extends ReactiveDropdownMappingsRefreshOptions<TModel> {
    readonly fieldNames: readonly string[];
    readonly mode?: ReactiveDropdownFieldRefreshMode;
}

export interface ReactiveDropdownFieldSourceSyncOptions<TModel, TEntity> {
    readonly formBuilder: ReactiveDropdownSyncFormBuilder<TModel> | null | undefined;
    readonly sections: readonly ReactiveDropdownFieldLookupSection<TModel>[] | null | undefined;
    readonly fieldName: string;
    readonly dropdown: DropdownData<TEntity>;
    readonly valuePrimaryKey?: string;
    readonly controlValueKey?: string;
    readonly backendValueKey?: string;
    readonly resetSelection?: boolean;
    readonly beforeRefresh?: () => void;
}

export interface ReactiveDropdownFieldSourceSyncResult<TModel> {
    readonly field: FormField<TModel> | null;
    readonly rebound: boolean;
    readonly refreshed: boolean;
}

export interface ReactiveDropdownSelectionSyncOptions<TModel, TEntity> {
    readonly formBuilder: ReactiveDropdownSyncFormBuilder<TModel> | null | undefined;
    readonly fieldName: string;
    readonly dropdown: DropdownData<TEntity> | null | undefined;
    readonly rawValue: unknown;
    readonly multiple?: boolean;
    readonly patchForm?: boolean;
    readonly emit?: boolean;
    readonly configuration?: ReactiveSelectionResolverConfiguration;
}

export interface ReactiveDropdownSelectionSyncResult<TEntity> {
    readonly selected: TEntity[];
    readonly value: DropdownSelection<TEntity>;
    readonly patched: boolean;
}

/**
 * Centralizes external dropdown synchronization used by host components.
 *
 * Host forms often need the same three steps after data hydration: resolve selected entities,
 * update the dropdown selection store and patch the reactive form control only when it differs.
 */
export class ReactiveDropdownSyncController {

    public static findField<TModel>(
        sections: readonly ReactiveDropdownFieldLookupSection<TModel>[] | null | undefined,
        fieldName: string,
    ): FormField<TModel> | null {
        for (const section of sections ?? []) {
            const found = section.fields?.find((field) => field.name === fieldName);

            if (found !== undefined) {
                return found;
            }
        }

        return null;
    }

    public static bindFieldData<TModel, TEntity>(options: ReactiveDropdownFieldBindingOptions<TModel, TEntity>): boolean {
        const dropdownConfiguration = options.field?.input.dropdownConfiguration;

        if (options.field === null || options.field === undefined || dropdownConfiguration === undefined) {
            return false;
        }

        options.field.input.data = options.dropdown;

        if (options.valuePrimaryKey !== undefined) {
            dropdownConfiguration.valuePrimaryKey = options.valuePrimaryKey;
        }

        if (options.controlValueKey !== undefined) {
            dropdownConfiguration.controlValueKey = options.controlValueKey;
        }

        if (options.backendValueKey !== undefined) {
            dropdownConfiguration.backendValueKey = options.backendValueKey;
        }

        return true;
    }

    public static syncFieldSource<TModel, TEntity>(
        options: ReactiveDropdownFieldSourceSyncOptions<TModel, TEntity>,
    ): ReactiveDropdownFieldSourceSyncResult<TModel> {
        const field = this.findField(options.sections, options.fieldName);
        const rebound = this.bindFieldData({
            field,
            dropdown: options.dropdown,
            ...(options.valuePrimaryKey === undefined ? {} : { valuePrimaryKey: options.valuePrimaryKey }),
            ...(options.controlValueKey === undefined ? {} : { controlValueKey: options.controlValueKey }),
            ...(options.backendValueKey === undefined ? {} : { backendValueKey: options.backendValueKey }),
        });

        if (options.resetSelection === true) {
            setDropdownSelected(options.dropdown, null);
        }

        this.refreshMappings({
            formBuilder: options.formBuilder,
            ...(options.beforeRefresh === undefined ? {} : { beforeRefresh: options.beforeRefresh }),
        });

        return {
            field,
            rebound,
            refreshed: options.formBuilder !== null && options.formBuilder !== undefined,
        };
    }

    public static refreshMappings<TModel>(options: ReactiveDropdownMappingsRefreshOptions<TModel>): void {
        options.beforeRefresh?.();
        options.formBuilder?.requestExternalDropdownMappingsRefresh();
    }

    public static refreshFieldMappings<TModel>(options: ReactiveDropdownFieldMappingsRefreshOptions<TModel>): void {
        const normalizedFieldNames = Array.from(new Set(
            options.fieldNames.map((fieldName) => fieldName.trim()).filter((fieldName) => fieldName !== ''),
        ));

        if (normalizedFieldNames.length === 0) {
            return;
        }

        options.beforeRefresh?.();

        if (options.mode === 'optionsOnly' && options.formBuilder?.requestExternalDropdownOptionMappingsRefreshForFields) {
            options.formBuilder.requestExternalDropdownOptionMappingsRefreshForFields(normalizedFieldNames);
            return;
        }

        if (options.formBuilder?.requestExternalDropdownMappingsRefreshForFields) {
            options.formBuilder.requestExternalDropdownMappingsRefreshForFields(normalizedFieldNames);
            return;
        }

        options.formBuilder?.requestExternalDropdownMappingsRefresh();
    }

    public static syncSelection<TModel, TEntity>(
        options: ReactiveDropdownSelectionSyncOptions<TModel, TEntity>,
    ): ReactiveDropdownSelectionSyncResult<TEntity> {
        const selected = ReactiveSelectionResolver.resolveSelectedEntities(
            options.dropdown,
            options.rawValue,
            options.configuration,
        );
        const value = this.createSelectionValue(selected, options.multiple === true);

        if (!options.dropdown || selected.length === 0) {
            return { selected, value, patched: false };
        }

        setDropdownSelected(options.dropdown, value);

        if (options.patchForm === false || !options.formBuilder?.model?.get(options.fieldName)) {
            return { selected, value, patched: false };
        }

        const currentValue = options.formBuilder.model.get(options.fieldName)?.value;

        if (ReactiveSelectionResolver.areSelectionsEquivalent(currentValue, value, options.configuration)) {
            return { selected, value, patched: false };
        }

        options.formBuilder.updateForm({ [options.fieldName]: value }, options.emit === true);

        return { selected, value, patched: true };
    }

    private static createSelectionValue<TEntity>(selected: readonly TEntity[], multiple: boolean): DropdownSelection<TEntity> {
        return multiple ? [...selected] : selected[0] ?? null;
    }
}
